import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    createContext,
    useContext,
} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import jwtDecode from 'jwt-decode';
import { notifications } from '@mantine/notifications';
import * as Sentry from '@sentry/react';

import { User } from '../codegen/graphql';
import { GET_USER } from '../apollo/queries/user';
import { LOGOUT } from '../apollo/mutations/auth';
import { refreshTokens } from '../apollo';
import { useAuthStore } from '../stores/auth.store';
import { useRequestsTableStore } from '../stores/RequestsTableStore';
import { Loader } from '../components/common';

export interface AuthContextType {
    isLoggedIn: boolean;
    role: string;
    authUser: User | null;
    logout: (addRedirect?: boolean) => Promise<void>;
    loadTokens: (skipRefresh?: boolean) => Promise<void>;
    loadingAuthUser: boolean;
    redirectUrl: string;
    setRedirectUrl: (url: string) => void;
    isImpersonating: boolean;
    switchBack: () => void;
}

const initialValues = {
    role: '',
    isLoggedIn: false,
    authUser: null,
    loadTokens: async () => {},
    logout: async () => {},
    loadingAuthUser: true,
    redirectUrl: '',
    setRedirectUrl: () => {},
    isImpersonating: false,
    switchBack: () => {},
};

const AuthContext = createContext<AuthContextType>(initialValues);

type Props = {
    router: any;
    children: React.ReactNode;
};

function AuthProvider({ children, router }: Props) {
    const [loadingAuthUser, setLoadingAuthUser] = useState(true);
    const [redirectUrl, setRedirectUrl] = useState('');

    const {
        isLoggedIn,
        userId,
        isImpersonating,
        setLoggedIn,
        setUserId,
        setStandInForUserIds,
        setIsImpersonating,
    } = useAuthStore();
    const { pathname, search } = window.location;
    const searchParams = new URLSearchParams(search);

    const [logoutMutation] = useMutation(LOGOUT);

    const {
        data: authUserData,
        error: authUserError,
        loading,
        refetch: refetchUser,
    } = useQuery(GET_USER, {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
        variables: {
            id: userId,
        },
        skip: !isLoggedIn,
    });

    const hasRetriedUserQuery = useRef(false);

    useEffect(() => {
        if (searchParams.has('redirectUrl')) {
            setRedirectUrl(searchParams.get('redirectUrl') ?? '');
        }
    }, [searchParams]);

    useEffect(() => {
        const user = authUserData?.getUserById;
        if (user) {
            Sentry.setUser({
                id: authUserData.getUserById?.id,
                email: authUserData.getUserById?.email,
                username: authUserData.getUserById?.username,
            });
        }
    }, [authUserData]);

    const logout = useCallback(async (addRedirect: boolean = false) => {
        try {
            await logoutMutation();
        } finally {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('superAdminAccessToken');
            localStorage.removeItem('superAdminRefreshToken');
            Sentry.setUser(null);
            setStandInForUserIds([]);
            setIsImpersonating(false);
            setUserId();
            setLoggedIn(false);
            setRedirectUrl('');
            useRequestsTableStore.getState().resetFilters();

            if (addRedirect && pathname !== '/auth/login' && pathname !== '/') {
                await router.navigate(
                    `/auth/login?redirectUrl=${encodeURI(pathname + search)}`,
                    { replace: true },
                );
            } else if (search) {
                await router.navigate(`/auth/login${search}`, {
                    replace: true,
                });
            } else {
                await router.navigate('/auth/login', { replace: true });
            }
        }
    }, []);

    const loadTokens = useCallback(async (skipRefresh = false) => {
        setLoadingAuthUser(true);
        const storageAccessToken = localStorage.getItem('accessToken');
        const storageRefreshToken = localStorage.getItem('refreshToken');

        if (storageRefreshToken && storageRefreshToken !== 'undefined') {
            let accessToken = storageAccessToken;
            if (!skipRefresh) {
                let accessTokenExpired = true;
                if (accessToken) {
                    const { exp } = jwtDecode<{ exp: number }>(accessToken);
                    const expirationDate = new Date(exp * 1000);
                    accessTokenExpired = expirationDate < new Date();
                }

                if (accessTokenExpired) {
                    try {
                        const result = await refreshTokens({ force: true });
                        if (!result) {
                            throw new Error('Failed to refresh tokens');
                        }
                        accessToken = result.accessToken;
                    } catch (error) {
                        if (isLoggedIn) {
                            console.error(
                                'Error refreshing tokens in AuthProvider. Session is probably expired. Error:',
                                error,
                            );
                            notifications.show({
                                title: 'Session expired',
                                message:
                                    'Your session has expired. Please log in again.',
                                color: 'red',
                            });
                            await logout(true);
                        }

                        setLoadingAuthUser(false);

                        return;
                    }
                }
            }

            if (accessToken) {
                const { userId: id, standInForUserIds } = jwtDecode<{
                    userId: string;
                    standInForUserIds: string[];
                }>(accessToken);
                setStandInForUserIds(standInForUserIds);
                setUserId(id);
            }
            setLoggedIn(true);

            const superAdminRefreshToken = localStorage.getItem(
                'superAdminRefreshToken',
            );
            setIsImpersonating(
                !!superAdminRefreshToken && superAdminRefreshToken !== '',
            );

            if (pathname.includes('auth')) {
                router.navigate(redirectUrl || '/', {
                    replace: true,
                });
            }
        } else if (!pathname.includes('auth')) {
            logout(true);
        }

        setLoadingAuthUser(false);
    }, []);

    const switchBack = useCallback(() => {
        const superAdminAccessToken = localStorage.getItem(
            'superAdminAccessToken',
        );
        const superAdminRefreshToken = localStorage.getItem(
            'superAdminRefreshToken',
        );

        if (superAdminAccessToken && superAdminRefreshToken) {
            localStorage.setItem('accessToken', superAdminAccessToken);
            localStorage.setItem('refreshToken', superAdminRefreshToken);
            localStorage.removeItem('superAdminAccessToken');
            localStorage.removeItem('superAdminRefreshToken');
            window.location.reload();
        }
    }, []);

    useEffect(() => {
        loadTokens();
    }, []);

    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === 'accessToken' && !event.newValue) {
                const { isLoggedIn: currentlyLoggedIn } =
                    useAuthStore.getState();
                if (currentlyLoggedIn) {
                    setLoggedIn(false);
                    setUserId();
                    setStandInForUserIds([]);
                    router.navigate('/auth/login', { replace: true });
                }
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const role = useMemo(() => {
        if (!authUserData?.getUserById) {
            return '';
        }

        return authUserData?.getUserById?.userRole?.role
            .toLowerCase()
            .replace(/\s/g, '');
    }, [authUserData]);

    const value = useMemo(
        () => ({
            isLoggedIn,
            loadingAuthUser: loadingAuthUser || loading,
            authUser: authUserData?.getUserById || null,
            loadTokens,
            role,
            logout,
            redirectUrl,
            setRedirectUrl,
            isImpersonating,
            switchBack,
        }),
        [
            isLoggedIn,
            authUserData,
            loadingAuthUser,
            loading,
            loadTokens,
            role,
            logout,
            redirectUrl,
            setRedirectUrl,
            isImpersonating,
            switchBack,
        ],
    );

    useEffect(() => {
        if (!authUserError) {
            hasRetriedUserQuery.current = false;
            return;
        }

        const isAuthError = authUserError.graphQLErrors?.some(
            err => err.extensions?.code === 'UNAUTHENTICATED',
        );

        if (!isAuthError) {
            console.error(
                'Non-auth error fetching user data (not logging out):',
                authUserError,
            );
            return;
        }

        if (hasRetriedUserQuery.current) {
            logout(true);
            return;
        }

        hasRetriedUserQuery.current = true;
        refetchUser();
    }, [authUserError, refetchUser, logout]);

    if (loadingAuthUser) {
        return <Loader isPageLoader />;
    }

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
